/**
 * Chapter-based onboarding tour definitions.
 *
 * Each chapter is a self-contained mini-tour (3-5 steps) focused on
 * one platform section. Chapters can be taken in any order.
 *
 * Steps without a `targetSelector` render as full-screen modals.
 * Steps with a `targetSelector` render as spotlight overlays.
 */

import type { LucideIcon } from 'lucide-react'
import type { FeatureFlagKey } from '@/admin/hooks/use-feature-flag'
import {
  LayoutDashboard,
  Package,
  Shield,
  FileText,
  ArrowLeftRight,
  MapPin,
  Calculator,
  Home,
  Hammer,
  Settings,
} from 'lucide-react'

export interface TourStep {
  id: string
  targetSelector?: string
  /** Navigate to this route before showing the step. */
  route?: string
  title: string
  body: string
  placement: 'top' | 'bottom' | 'left' | 'right'
}

export interface TourChapter {
  id: string
  title: string
  description: string
  icon: LucideIcon
  /** Route to navigate to when starting this chapter. */
  route: string
  steps: TourStep[]
  /** When true, the chapter card shows "Coming Soon" and is not clickable. */
  comingSoon?: boolean
  /** Group key for clustering related chapters (e.g. all claim types under "claims"). */
  group?: string
  /**
   * Feature flags that gate this chapter. The chapter is shown only when at
   * least one of these flags is enabled for the tenant. Omit to always show.
   */
  flags?: FeatureFlagKey[]
}

export const TOUR_CHAPTERS: TourChapter[] = [
  // ── Chapter 1: Dashboard ──
  {
    id: 'dashboard',
    title: 'Your Dashboard',
    description: 'Stats, trends, and recent activity at a glance',
    icon: LayoutDashboard,
    route: '/dashboard',
    steps: [
      {
        id: 'stats-cards',
        targetSelector: '[data-tour="stats-cards"]',
        title: 'Your business at a glance',
        body: 'These four numbers are your command centre. Total Warranties is your book size. Claims Rate shows book health. Active Dealers is your network reach. Open Claims flags what needs attention now.',
        placement: 'bottom',
      },
      {
        id: 'trends',
        targetSelector: '[data-tour="trends"]',
        title: 'Spot patterns over time',
        body: 'Registration Trend shows how your warranty book is growing. Claims Trend helps you catch spikes early. Use the time filter to zoom into any period.',
        placement: 'bottom',
      },
      {
        id: 'recent-activity',
        targetSelector: '[data-tour="recent-activity"]',
        title: 'Recent activity & alerts',
        body: 'Recent Registrations shows the latest warranties filed. Alerts flag anything that needs your attention, like expiring warranties, overdue claims, and dormant dealers.',
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 2: Product Library ──
  {
    id: 'products',
    title: 'Product Library',
    description: 'Your product catalogue, categories, and SKU variants',
    icon: Package,
    route: '/manage/products/library',
    flags: ['products'],
    steps: [
      {
        id: 'product-library',
        targetSelector: '[data-tour="product-library"]',
        title: 'Your product catalogue',
        body: 'This is your product library, where every product your organisation sells and warrants lives. You can search, filter, and switch between list and grid views. Each product connects to a warranty template that defines its coverage terms.',
        placement: 'bottom',
      },
      {
        id: 'product-categories',
        targetSelector: '[data-tour="product-categories"]',
        title: 'Organised by category',
        body: 'Products are grouped into categories like Shingles, Accessories, or Components. Categories help your team find the right product fast and let you apply warranty rules to whole groups at once.',
        placement: 'bottom',
      },
      {
        id: 'product-structure',
        targetSelector: '[data-tour="product-structure"]',
        title: 'Product lines and variants',
        body: 'Each row is a product line. Click the arrow to expand and see individual variants, like colour SKUs. Warranty rules apply at the product line level, so you set coverage once and it flows down to every variant.',
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 3: Warranties & Rules ──
  {
    id: 'warranties',
    title: 'Warranties & Rules',
    description: 'Standard vs extended warranties, and how rules map them to products',
    icon: Shield,
    route: '/manage/warranties/templates',
    flags: ['warranties'],
    steps: [
      {
        id: 'warranty-list',
        targetSelector: '[data-tour="warranty-list"]',
        title: 'Warranty templates',
        body: "Each row is a warranty template, a blueprint for coverage. You'll see Standard warranties (manufacturer-backed) and Extended warranties (additional paid coverage). The status column shows which are Active, Draft, or Archived.",
        placement: 'bottom',
      },
      {
        id: 'warranty-types',
        targetSelector: '[data-tour="warranty-types"]',
        title: 'Standard vs extended',
        body: 'Standard warranties come with the product. Extended warranties are purchased separately for longer or broader coverage. Each template defines the coverage scope: Material Only or Material and Labour.',
        placement: 'bottom',
      },
      {
        id: 'rule-hierarchy',
        title: 'Application rules',
        body: 'Rules connect warranties to products. The hierarchy works top-down: Global rules are the fallback, then Template, Category, and Product-level rules get more specific. A Product-level rule always wins over a Global one.',
        placement: 'bottom',
      },
      {
        id: 'rule-mapping',
        title: 'How mapping works',
        body: "When a user registers a warranty, the system checks which rules apply to that product. It finds the most specific match and offers those warranty options. Conditions like property type or geo zone can further narrow what's available.",
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 4: Registrations ──
  {
    id: 'registrations',
    title: 'Registrations',
    description: 'How warranties get registered, the wizard flow, and selection logic',
    icon: FileText,
    route: '/registrations',
    flags: ['registrations'],
    steps: [
      {
        id: 'registration-list',
        targetSelector: '[data-tour="registration-list"]',
        title: 'Registration list',
        body: 'Every warranty registration lives here. You can search, filter by status, product, or date, and click any row to see the full registration detail.',
        placement: 'bottom',
      },
      {
        id: 'registration-actions',
        targetSelector: '[data-tour="registration-actions"]',
        title: 'Start a new registration',
        body: "Click '+ New Registration' to start a new warranty. The wizard walks you through: select the property, choose the product, enter customer details, pick the warranty, and review.",
        placement: 'bottom',
      },
      {
        id: 'warranty-selection',
        title: 'Warranty selection',
        body: 'During registration, the system automatically shows which warranties are available for the selected product based on your application rules, geo zones, and user permissions. Standard warranties appear by default; extended warranties may require additional authorisation.',
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 5: Claims (grouped sub-chapters) ──

  {
    id: 'claims-home',
    group: 'claims',
    title: 'Claims Home',
    description: 'Your claims list, filters, and the lifecycle every claim follows',
    icon: Home,
    route: '/claims',
    flags: ['claims'],
    steps: [
      {
        id: 'claims-list',
        targetSelector: '[data-tour="claims-list"]',
        title: 'Home for every claim',
        body: 'This is your claims command center. Every warranty claim lives here. Filter by status, assignee, or date range. Each row shows the claim type so your team can prioritize at a glance.',
        placement: 'bottom',
      },
    ],
  },

  {
    id: 'claims-engine',
    group: 'claims',
    title: 'Engine Room',
    description: 'Claim configuration, warranties, geographic pricing, and field rules',
    icon: Settings,
    route: '/manage/claims/schemas',
    flags: ['claims'],
    steps: [
      {
        id: 'engine-config',
        targetSelector: '[data-tour="claims-config"]',
        title: 'Your claims engine room',
        body: 'This is where claim types are configured. Each schema defines the fields, workflows, and rules for a category of claims. What your team sees on the front end is driven entirely by what is set up here. No guesswork, no inconsistency.',
        placement: 'bottom',
      },
      {
        id: 'engine-proration',
        route: '/manage/warranties/proration',
        targetSelector: '[data-tour="proration-header"]',
        title: 'Proration: coverage over time',
        body: 'When a claim is filed years after installation, proration calculates what the warranty still covers. Every formula is set here, from long-life products with full coverage for decades down to standard declining schedules. The system applies the right formula automatically.',
        placement: 'bottom',
      },
      {
        id: 'engine-geo',
        route: '/manage/geography/zones',
        targetSelector: '[data-tour="geo-zones-list"]',
        title: 'Geographic zones set the price',
        body: 'Material and labor costs vary by region. The system maps every property to a pricing zone through ZIP code lookup so settlements reflect real local costs. No manual lookups, no pricing errors.',
        placement: 'bottom',
      },
    ],
  },

  {
    id: 'claims-filing',
    group: 'claims',
    title: 'The Claims Lifecycle',
    description: 'Filing, workflow, lab, settlement, proration, pricing, and payment',
    icon: Hammer,
    route: '/claims/submit',
    flags: ['claims'],
    steps: [
      // ── Filing flow ──
      {
        id: 'filing-type',
        targetSelector: '[data-tour="claim-type-selector"]',
        title: 'Every claim starts with a type',
        body: 'Select the claim type that matches the situation. Each type determines the workflow, cost routing, and stages your claim will follow. The process begins the moment you make your selection.',
        placement: 'bottom',
      },
      {
        id: 'filing-registration',
        route: '/claims/submit?tourStep=holding',
        targetSelector: '[data-tour="registration-selector"]',
        title: 'Link to a warranty registration',
        body: 'If the structure is already registered, you can kick start a claim straight away. If not, the system will route you back to register the structure first, then bring you back to continue filing the claim.',
        placement: 'right',
      },
      {
        id: 'filing-details',
        route: '/claims/submit?tourStep=details',
        targetSelector: '[data-tour="claim-details-form"]',
        title: 'Fill in claim details',
        body: 'Enter the specifics: what went wrong, when it was noticed, and photos of the damage. These details drive the downstream analysis and help the lab team assess the claim accurately.',
        placement: 'right',
      },
      {
        id: 'filing-submit',
        title: 'Submit and the workflow begins',
        body: "Once you hit submit, the claim is live. It enters an automated workflow, a series of stations, each owned by a specific role. Let's open a real claim to see it in action.",
        placement: 'bottom',
      },

      // ── Live claim: stepper, actions, signpost, tabs ──
      {
        id: 'workflow-stepper',
        route: '/claims?tourStep=workflow',
        targetSelector: '[data-tour="workflow-stepper"]',
        title: 'The workflow stepper',
        body: "Every claim moves through a defined series of stations. The stepper shows exactly where the claim is right now and what's ahead. Each station is colour-coded by role: orange for the Resolution Specialist, purple for Lab, red for the Lead, green for the Settlement Specialist.",
        placement: 'bottom',
      },
      {
        id: 'workflow-actions',
        targetSelector: '[data-tour="claim-actions"]',
        title: 'Context-aware action buttons',
        body: 'These buttons change at every station. Only the actions that are valid right now are shown, and only the person responsible can execute them. The system tells you exactly what to do next.',
        placement: 'bottom',
      },
      {
        id: 'workflow-signpost',
        targetSelector: '[data-tour="claim-signpost"]',
        title: 'Guidance at every step',
        body: "This banner tells the current owner what's happening and what's expected. It updates automatically as the claim moves through the workflow, from waiting on documents, to lab analysis, to resolution and settlement.",
        placement: 'top',
      },
      {
        id: 'workflow-tabs',
        targetSelector: '[data-tour="claim-tabs"]',
        title: 'Specialist workspaces',
        body: 'Each tab is a workspace for a different specialist. The Overview holds the full claim picture - defect details, claim context, contacts, and contractor - and is where intake and cost review happen. Lab runs defect analysis. Inspection tracks site visits. Settlement reviews line items and payments. LOA manages approval for claims over defined thresholds. The claim steers the user to the right tab automatically.',
        placement: 'bottom',
      },

      // ── Lab tab ──
      {
        id: 'tab-lab',
        targetSelector: '[data-tour="tab-lab"]',
        title: 'Lab: structured defect analysis',
        body: "When photos and samples arrive, the Lab Technician logs each sample, records measurements, and runs analysis using structured decision matrices. There are 33 matrices (SW001 through SW033), one per defect type. Each maps defect sub-types against installation age to support a claim decision.",
        placement: 'bottom',
      },

      // ── Inspection tab ──
      {
        id: 'tab-inspection',
        targetSelector: '[data-tour="tab-inspection"]',
        title: 'Inspection: when a site visit is required',
        body: "Not every claim needs an inspection, but when one is flagged, this tab activates. An inspector is assigned, schedules a site visit, documents roof condition, photographs damage, and records findings. The inspection report feeds directly into the claim analysis and helps the team make an informed decision.",
        placement: 'bottom',
      },

      // ── Settlement tab ──
      {
        id: 'tab-settlement',
        targetSelector: '[data-tour="tab-settlement"]',
        title: 'Settlement: every dollar accounted for',
        body: "Settlements are built from line items across 21 SAP-aligned categories covering material, labour, and fees. Each line item is automatically routed to the correct GL account: 3626 (warranty reserve) or 5490 (goodwill), with profit centres assigned by product line and geographic zone. The Refresh Prices button recalculates all items using the latest zone rates.",
        placement: 'bottom',
      },
      {
        id: 'settlement-payment',
        targetSelector: '[data-tour="tab-settlement"]',
        title: 'Eight payment types, validated in real time',
        body: "The system supports eight payment types: Material Voucher, Labour Voucher, Cash Settlement, Replacement Product, Repair Credit, Mixed Settlement, Dealer Credit, and Write-Off. Each has validation rules tied to line items. A Material Voucher cannot be applied against labour. A Mixed Settlement can combine types. The system enforces these rules so invalid payments cannot be created.",
        placement: 'bottom',
      },

      // ── LOA tab ──
      {
        id: 'tab-loa',
        targetSelector: '[data-tour="tab-loa"]',
        title: 'LOA: tiered approval before settlement',
        body: "Before any settlement is paid, it must pass through LOA approval. Every business unit has up to four approval tiers based on dollar thresholds. Lower-value claims are approved by a Team Leader. As the amount increases, approval escalates to Manager, Director, and VP. The LOA tab shows the current approval status, who needs to approve, and the settlement summary under review.",
        placement: 'bottom',
      },

      // ── Proration (modal, conceptual) ──
      {
        id: 'proration-explained',
        title: 'How proration calculates coverage over time',
        body: "When a claim is filed years after installation, proration determines the remaining coverage. Three formula types: Tiered (different rates per year range, e.g. 100% for years 1-10, then 90% for 11-15), Linear (steady annual decline), and Flat (fixed rate regardless of age). The system selects the formula from the warranty template and calculates automatically using the installation date and decision date.",
        placement: 'bottom',
      },

      // ── Automation (modal, conceptual) ──
      {
        id: 'automation-audit',
        title: 'Automated from end to end',
        body: "Every workflow transition can trigger emails automatically: claim confirmations, document requests, lab results, settlement offers, LOA letters, and payment receipts. A full audit trail tracks every action: who did what, when, and why.",
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 6: Transfers & Exceptions ──
  {
    id: 'transfers-exceptions',
    title: 'Transfers & Exceptions',
    description: 'Warranty transfers, exception requests, and approval workflows',
    icon: ArrowLeftRight,
    route: '/transfers',
    flags: ['transfers', 'exceptions'],
    steps: [
      {
        id: 'transfers-list',
        targetSelector: '[data-tour="transfers-list"]',
        title: 'Warranty transfers',
        body: 'When a property changes ownership, the warranty can transfer to the new owner. Transfer rules like fees, time windows, and eligibility are set on each warranty template.',
        placement: 'bottom',
      },
      {
        id: 'exceptions',
        title: 'Exception requests',
        body: 'Sometimes a warranty registration needs to go outside standard rules. Think late registrations, coverage extensions, or fee waivers. Exception requests are filed and require admin approval.',
        placement: 'bottom',
      },
      {
        id: 'exception-approval',
        title: 'Approval workflow',
        body: 'Exception requests show the reason, the registration they apply to, and who requested them. Admins can approve or deny. Every exception is tracked for audit so nothing slips through informally.',
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 7: Geographic Availability ──
  {
    id: 'geo',
    title: 'Geographic Availability',
    description: 'Geo zones, plant service areas, and postcode-based availability',
    icon: MapPin,
    route: '/manage/geography/zones',
    flags: ['geographic_availability'],
    steps: [
      {
        id: 'geo-zones-list',
        targetSelector: '[data-tour="geo-zones-list"]',
        title: 'Geo zones overview',
        body: 'Geographic zones control where your warranties and products are available. Zones can be countries, states, regions, districts, or custom areas like Plant Service Areas.',
        placement: 'bottom',
      },
      {
        id: 'zone-types',
        targetSelector: '[data-tour="zone-types"]',
        title: 'Zone types',
        body: 'Plant Service Areas define which manufacturing plant serves a region. Geo Zones are custom boundaries you define. Both can contain postal codes that determine availability during registration.',
        placement: 'bottom',
      },
      {
        id: 'geo-how-it-works',
        title: 'How geography works',
        body: 'When a customer registers a warranty, their address is checked against active zones. Only products and warranties available in their zone are offered. This ensures the right coverage for the right region.',
        placement: 'bottom',
      },
    ],
  },

  // ── Chapter 8: Proration & Coverage ──
  {
    id: 'proration',
    title: 'Proration & Coverage',
    description: 'How coverage is calculated over the warranty term',
    icon: Calculator,
    route: '/manage/warranties/proration',
    flags: ['proration_formulas'],
    steps: [
      {
        id: 'proration-list',
        targetSelector: '[data-tour="proration-list"]',
        title: 'Proration formulas',
        body: 'Proration calculates how much coverage remains when a claim is filed partway through a warranty term. Three formula types: Tiered (different rates per year range), Linear (even decline over time), and Flat (fixed rate regardless of time).',
        placement: 'bottom',
      },
      {
        id: 'coverage-scope',
        title: 'Coverage scope',
        body: 'Each formula specifies Material Only or Material and Labour coverage. Material covers product replacement costs. Labour covers installation or repair work. The formula determines what percentage of each is covered at any point in the warranty term.',
        placement: 'bottom',
      },
      {
        id: 'proration-calculator',
        targetSelector: '[data-tour="proration-calculator"]',
        title: 'Try it yourself',
        body: 'Use the built-in calculator to test any formula. Enter years elapsed and costs to see exactly what the customer would receive. This is how your claims team calculates settlements.',
        placement: 'bottom',
      },
    ],
  },
]
